import { useCallback, useContext, useEffect, useReducer } from 'react';
import { Just, Nothing } from 'purify-ts/Maybe';
import { buildStateMachine } from '../stateMachine';
import { BrowserSearchContext } from '../provider';
const initialState = {
    status: 'idle',
    isFetching: false,
};
const fromIdleToLoading = (state, action) => (state.status === 'idle' && action.type === 'requestStarted' ?
    Just({
        status: 'loading',
        request: action.request,
        isFetching: true,
    }) : Nothing);
const fromLoadingToLoading = (state, action) => (state.status === 'loading' && action.type === 'requestStarted' ?
    Just({
        status: 'loading',
        request: action.request,
        isFetching: true,
    }) : Nothing);
const fromLoadingToError = (state, action) => (state.status === 'loading' && action.type === 'requestFailed' && state.request === action.request ?
    Just({
        status: 'error',
        request: action.request,
        error: action.error,
        isFetching: false,
    }) : Nothing);
const fromLoadingToSuccess = (state, action) => (state.status === 'loading' && action.type === 'requestCompleted' && state.request === action.request ?
    Just({
        status: 'success',
        request: action.request,
        response: action.response,
        isFetching: false,
    }) : Nothing);
const fromSuccessToStale = (state, action) => (state.status === 'success' && action.type === 'requestStarted' ?
    Just({
        status: 'stale',
        request: state.request,
        response: state.response,
        newRequest: action.request,
        isFetching: true,
    }) : Nothing);
const fromStaleToSuccess = (state, action) => (state.status === 'stale' && action.type === 'requestCompleted' && state.newRequest === action.request ?
    Just({
        status: 'success',
        request: action.request,
        response: action.response,
        isFetching: false,
    }) : Nothing);
const fromStaleToStale = (state, action) => (state.status === 'stale' && action.type === 'requestStarted' ?
    Just({
        status: 'stale',
        request: state.request,
        response: state.response,
        newRequest: action.request,
        isFetching: true,
    }) :
    Nothing);
const fromStaleToError = (state, action) => (state.status === 'stale' && action.type === 'requestFailed' && state.newRequest === action.request ?
    Just({
        status: 'error',
        request: action.request,
        error: action.error,
        isFetching: false,
    }) : Nothing);
const fromErrorToLoading = (state, action) => (state.status === 'error' && action.type === 'requestStarted' ?
    Just({
        status: 'loading',
        request: action.request,
        isFetching: true,
    }) : Nothing);
export const buildReducer = () => {
    const stateTransitions = [fromIdleToLoading, fromLoadingToLoading, fromLoadingToError, fromLoadingToSuccess, fromSuccessToStale, fromStaleToStale, fromStaleToSuccess, fromStaleToError, fromErrorToLoading];
    return buildStateMachine(stateTransitions);
};
export const useIndexValues = ({ storeId, field }) => {
    const queryClient = useContext(BrowserSearchContext);
    const [state, dispatch] = useReducer(buildReducer(), initialState);
    const runQuery = useCallback(() => {
        const request = {
            storeId,
            field,
        };
        const responsePromise = queryClient.queryIndex(request);
        dispatch({ type: 'requestStarted', request });
        responsePromise
            .then(response => {
            dispatch({ type: 'requestCompleted', response: response, request });
        })
            .catch(error => {
            dispatch({ type: 'requestFailed', request, error });
        });
    }, [storeId, field]);
    useEffect(() => {
        queryClient.subscribeToStoreChange(storeId)(runQuery);
        return () => {
            queryClient.unsubscribeToStoreChange(storeId)(runQuery);
        };
    }, [storeId, queryClient, runQuery]);
    useEffect(() => {
        runQuery();
    }, [runQuery]);
    return state;
};
